import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, PHONE, IMAGES } from "../lib/api";
import { useSEO } from "../lib/useSEO";
import { Reveal } from "../components/Reveal";
import { Breadcrumbs, CtaBand, FaqAccordion, ICONS } from "../components/Shared";
import { CallButton, PhoneBox } from "../components/Layout";
import ContactForm from "../components/ContactForm";
import ReviewsSection from "../components/Reviews";
import { Check, Wrench, ArrowUpRight } from "lucide-react";

const IMG = { "depannage-serrurerie": IMAGES.van, "ouverture-de-porte": IMAGES.techHandle, "changement-de-serrure": IMAGES.cylinder, "blindage-de-porte": IMAGES.handleDark, "remplacement-de-porte": IMAGES.handleDark, "reparation-apres-effraction": IMAGES.tech, "mise-en-securite": IMAGES.techHandle };

export default function ServicePage() {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [services, setServices] = useState([]);
  const [faq, setFaq] = useState([]);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/services/${slug}`).then(r => setService(r.data)).catch(() => setService(false));
    api.get("/services").then(r => setServices(r.data)).catch(() => {});
    api.get("/faq").then(r => setFaq(r.data.slice(0, 5))).catch(() => {});
    api.get("/cities").then(r => setCities(r.data)).catch(() => {});
  }, [slug]);

  const serviceFaq = service && service.faq && service.faq.length ? service.faq : [];
  const allFaq = [...serviceFaq, ...faq].slice(0, 8);

  useSEO({
    title: service ? `${service.name} en Seine-et-Marne (77) | Serrurier 7j/7 - Devis gratuit` : "Service serrurerie",
    description: service ? `${service.intro.slice(0, 150)} Intervention 7j/7 dans le 77 ☎ ${PHONE}.` : "",
    path: `/services/${slug}`, faq: allFaq, service: service || null,
    keywords: service ? [`${service.name} Seine-et-Marne`, `${service.name} 77`, `serrurier ${service.name.toLowerCase()} 77`, "dépannage serrurier Seine-et-Marne", "urgence serrurier 77", `serrurier ${PHONE}`] : null,
  });

  if (service === false) return <NotFound />;
  if (!service) return <div className="pt-40 pb-40 text-center text-zinc-400 bg-white">Chargement…</div>;
  const Icon = ICONS[service.icon] || Wrench;
  const others = services.filter(s => s.slug !== slug);

  return (
    <div className="bg-white">
      <section className="relative pt-32 pb-16 sm:pt-36 hero-grid">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <Breadcrumbs items={[{ label: "Accueil", to: "/" }, { label: "Services", to: "/services" }, { label: service.name }]} />
            <div className="inline-flex items-center gap-2 rounded-xl bg-primary/10 p-3 mb-6"><Icon className="h-7 w-7 text-primary" /></div>
            {service.priority && <span className="inline-block ml-3 mb-6 rounded-full bg-primary px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-white align-middle">Urgence n°1</span>}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-semibold tracking-tighter leading-[0.98] text-zinc-900">{service.name}</h1>
            <p className="text-lg text-zinc-600 mt-6 max-w-xl leading-relaxed">{service.intro}</p>
            <div className="mt-8 flex flex-wrap items-center gap-4"><PhoneBox /><Link to="/contact" className="rounded-full border border-zinc-300 bg-white px-6 py-4 text-sm font-medium text-zinc-800 hover:border-primary hover:text-primary transition-colors">Devis gratuit</Link></div>
          </div>
          <Reveal delay={0.1} className="relative">
            <div className="rounded-3xl overflow-hidden shadow-2xl shadow-zinc-300/60 border border-zinc-200">
              <img src={IMG[slug] || IMAGES.tech} alt={`${service.name} par SERRURERIE 77 en Seine-et-Marne`} className="w-full h-[380px] sm:h-[460px] object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="relative z-10 py-16 border-y border-zinc-200 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid sm:grid-cols-2 gap-5">
          {service.points.map((p, i) => (
            <Reveal key={i} delay={i * 0.05} className="flex items-start gap-4 rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm">
              <span className="grid place-items-center h-8 w-8 rounded-full bg-primary/10 shrink-0"><Check className="h-4 w-4 text-primary" /></span>
              <span className="text-zinc-800">{p}</span>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="relative z-10 py-24 sm:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-start">
          <Reveal>
            <p className="overline mb-4">Devis gratuit</p>
            <h2 className="text-4xl sm:text-5xl font-display font-semibold tracking-tighter leading-none text-zinc-900">Besoin d'un {service.name.toLowerCase()} ?</h2>
            <p className="text-zinc-600 mt-6 leading-relaxed max-w-md">Demandez votre devis gratuit ou appelez-nous pour une intervention immédiate partout en Seine-et-Marne.</p>
            <div className="mt-10">
              <p className="overline mb-4">Autres services</p>
              <div className="flex flex-wrap gap-2.5">{others.map(o => <Link key={o.slug} to={`/services/${o.slug}`} className="rounded-full border border-zinc-200 bg-white px-4 py-2 text-sm text-zinc-700 hover:text-primary hover:border-primary transition-colors flex items-center gap-1.5 shadow-sm">{o.name}<ArrowUpRight className="h-3 w-3" /></Link>)}</div>
            </div>
          </Reveal>
          <Reveal delay={0.1}><ContactForm services={services} cities={cities} defaultService={service.name} /></Reveal>
        </div>
      </section>

      <ReviewsSection bg="bg-zinc-50" />
      <FaqAccordion items={allFaq} title={`Questions fréquentes — ${service.name}`} />
      <CtaBand title={`${service.name} — intervention rapide`} />
    </div>
  );
}

const NotFound = () => (
  <div className="pt-48 pb-32 text-center bg-white">
    <h1 className="text-4xl font-display text-zinc-900">Service introuvable</h1>
    <Link to="/services" className="text-primary mt-4 inline-block">← Retour aux services</Link>
  </div>
);
